import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, isSupabaseReady } from '../supabase';
import { useAuth } from './AuthContext';

export const UsersContext = createContext();

export const ROLES = {
  superadmin: { label: 'Super Administrador', level: 3 },
  admin: { label: 'Administrador', level: 2 },
  sales: { label: 'Ventas', level: 1 },
};

export const canManageUser = (actorRole, targetRole) => {
  if (actorRole === 'superadmin') return true;
  if (actorRole === 'admin') return targetRole !== 'superadmin';
  return false;
};

export const visibleUsersFor = (users, actorRole) => {
  if (actorRole === 'superadmin') return users;
  return users.filter(u => u.role !== 'superadmin');
};

export const hasOtherActiveSuperAdmin = (users, excludeId) =>
  users.some(u => u.role === 'superadmin' && u.status === 'active' && u.id !== excludeId);

/** Normalize a raw profile row so it always has camelCase `createdAt` */
const normalizeProfile = (row) => ({
  ...row,
  createdAt: row.createdAt || row.created_at || null,
});

const FALLBACK_USERS = [
  { id: 'u0', name: 'Dev Root',    email: 'dev@system.internal', password: 'superadmin123', role: 'superadmin', status: 'active', createdAt: new Date(Date.now() - 86400000 * 60).toISOString() },
  { id: 'u1', name: 'Sarah Owner', email: 'sarah@company.com',   password: 'admin123',      role: 'admin',      status: 'active', createdAt: new Date(Date.now() - 86400000 * 30).toISOString() },
  { id: 'u2', name: 'Mark Sales',  email: 'mark@company.com',    password: 'sales123',      role: 'sales',      status: 'active', createdAt: new Date(Date.now() - 86400000 * 10).toISOString() },
  { id: 'u3', name: 'Lisa Sales',  email: 'lisa@company.com',    password: 'sales123',      role: 'sales',      status: 'paused', createdAt: new Date(Date.now() - 86400000 * 5).toISOString()  },
];

export const UsersProvider = ({ children }) => {
  const { profile, login: authLogin, logout: authLogout, loading: authLoading, isSupabaseReady } = useAuth();

  const [supabaseUsers, setSupabaseUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [localUsers, setLocalUsers] = useState(
    () => JSON.parse(localStorage.getItem('crm_users')) || FALLBACK_USERS
  );
  const [currentUserId, setCurrentUserId] = useState(null);

  // ── Supabase: fetch all visible profiles ──────────────────────────────────
  const fetchSupabaseUsers = async () => {
    if (!isSupabaseReady || !profile) return;
    if (!['admin', 'superadmin'].includes(profile.role)) return;

    setUsersLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[UsersContext] fetchSupabaseUsers: error', error.message);
      } else {
        // Normalize every row so createdAt is always present
        setSupabaseUsers((data || []).map(normalizeProfile));
      }
    } catch (err) {
      console.error('[UsersContext] fetchSupabaseUsers: unexpected error', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (isSupabaseReady && profile) fetchSupabaseUsers();
  }, [isSupabaseReady, profile]);

  // ── Derived current user ──────────────────────────────────────────────────
  const currentUser = isSupabaseReady
    ? (profile ? normalizeProfile(profile) : null)
    : localUsers.find(u => u.id === currentUserId) || null;

  // ── LOGIN ─────────────────────────────────────────────────────────────────
  const loginUser = async (email, password) => {
    if (!isSupabaseReady) {
      const user = localUsers.find(
        u => u.email === email && u.password === password && u.status === 'active'
      );
      if (user) setCurrentUserId(user.id);
      return user || null;
    }
    const result = await authLogin(email, password);
    if (result.error) return null;
    return result.profile;
  };

  // ── LOGOUT ────────────────────────────────────────────────────────────────
  const logoutUser = async () => {
    if (!isSupabaseReady) { setCurrentUserId(null); return; }
    await authLogout();
    setSupabaseUsers([]);
  };

  // ── GET VISIBLE USERS ─────────────────────────────────────────────────────
  const getVisibleUsers = () => {
    if (isSupabaseReady) return visibleUsersFor(supabaseUsers, currentUser?.role);
    return visibleUsersFor(localUsers, currentUser?.role);
  };

  // ── GET USER BY ID ────────────────────────────────────────────────────────
  const getUserById = (id) => {
    if (isSupabaseReady) return supabaseUsers.find(u => u.id === id) || null;
    return localUsers.find(u => u.id === id) || null;
  };

  // ── ADD USER ──────────────────────────────────────────────────────────────
  const addUser = async (data) => {
    if (!isSupabaseReady) {
      const safeRole = data.role === 'superadmin' ? 'admin' : data.role;
      const updated = [
        ...localUsers,
        { ...data, role: safeRole, id: Date.now().toString(), createdAt: new Date().toISOString(), status: 'active' },
      ];
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }
    console.warn('[UsersContext] addUser via Supabase requires a server-side Edge Function.');
    return { error: 'User creation via Supabase requires an Edge Function. See setup guide.' };
  };

  // ── UPDATE USER ───────────────────────────────────────────────────────────
  const updateUser = async (id, updates) => {
    if (!isSupabaseReady) {
      const safeUpdates = { ...updates };
      if (currentUser?.role !== 'superadmin' && safeUpdates.role === 'superadmin') delete safeUpdates.role;
      const updated = localUsers.map(u => u.id === id ? { ...u, ...safeUpdates } : u);
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }
    const safeUpdates = { ...updates };
    delete safeUpdates.password;
    if (currentUser?.role !== 'superadmin') delete safeUpdates.role;
    const { error } = await supabase.from('profiles').update(safeUpdates).eq('id', id);
    if (!error) await fetchSupabaseUsers();
    return { error: error?.message || null };
  };

  // ── CHANGE PASSWORD ───────────────────────────────────────────────────────
  const changePassword = async (id, _newPassword) => {
    if (!isSupabaseReady) {
      const updated = localUsers.map(u =>
        u.id === id ? { ...u, passwordChangedAt: new Date().toISOString() } : u
      );
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return { error: null };
    }
    console.warn('[UsersContext] changePassword requires a server-side Edge Function.');
    return { error: 'Password changes require an Edge Function. See setup guide.' };
  };

  // ── SET STATUS ────────────────────────────────────────────────────────────
  const setStatus = async (id, status) => {
    const allUsers = isSupabaseReady ? supabaseUsers : localUsers;
    const target = allUsers.find(u => u.id === id);
    if (!target) return 'Usuario no encontrado.';

    const deactivating = ['paused', 'blocked', 'archived'];
    if (
      target.role === 'superadmin' &&
      deactivating.includes(status) &&
      !hasOtherActiveSuperAdmin(allUsers, id)
    ) {
      return 'Debe permanecer al menos un Super Administrador activo. Agrega otro primero.';
    }

    if (!isSupabaseReady) {
      const updated = localUsers.map(u => u.id === id ? { ...u, status } : u);
      setLocalUsers(updated);
      localStorage.setItem('crm_users', JSON.stringify(updated));
      return null;
    }

    const { error } = await supabase.from('profiles').update({ status }).eq('id', id);
    if (error) return error.message;
    await fetchSupabaseUsers();
    return null;
  };

  const isLoading = authLoading;

  return (
    <UsersContext.Provider value={{
      users: isSupabaseReady ? supabaseUsers : localUsers,
      currentUser,
      currentUserId,
      setCurrentUserId,
      loginUser,
      logoutUser,
      getVisibleUsers,
      getUserById,
      addUser,
      updateUser,
      changePassword,
      setStatus,
      isLoading,
      usersLoading,
      isSupabaseReady,
      refreshUsers: fetchSupabaseUsers,
    }}>
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => useContext(UsersContext);